/*{ "layout": "full-width" }*/
import { Component, type ComponentAttrs, elements } from "tosijs";

const { slot } = elements;

function easeInOutQuad(t: number): number {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Nearest enclosing <tosi-map> ancestor, or a <tosi-map> sibling at any level. */
function findMap(el: HTMLElement): any {
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    if (node.tagName === "TOSI-MAP") return node;
    for (const child of Array.from(node.children)) {
      if (child !== el && child.tagName === "TOSI-MAP") return child;
    }
    node = node.parentElement;
  }
  return null;
}

type MapWaypoint = { progress: number; lat: number; lng: number; zoom: number };

/**
 * Read <tosi-waypoint> children. Each carries a `coords="lat,lng,zoom"` (matching
 * <tosi-map>'s own attribute), or separate `lat`/`lng`/`zoom` attributes.
 */
function readMapWaypoints(host: HTMLElement): MapWaypoint[] {
  return Array.from(host.querySelectorAll("tosi-waypoint"))
    .map((wp) => {
      const parts = (wp.getAttribute("coords") || "")
        .split(",")
        .map((n) => Number(n.trim()));
      const at = (i: number, attr: string, fallback: number) =>
        Number.isFinite(parts[i]) ? parts[i] : Number(wp.getAttribute(attr) ?? fallback);
      return {
        progress: Number(wp.getAttribute("progress") || 0),
        lat: at(0, "lat", 0),
        lng: at(1, "lng", 0),
        zoom: at(2, "zoom", 1),
      };
    })
    .sort((a, b) => a.progress - b.progress);
}

/*#
# `<tosi-scroll-map>`

Waypoint-driven scroll controller for a tosijs-ui [`<tosi-map>`](https://ui.tosijs.net/mapbox)
(Mapbox). Inside a `<tosi-map>` within a section, each `<tosi-waypoint coords="lat,lng,zoom">`
becomes a keyframe; as the section pins, the map flies between them — pure declarative HTML, no
scroll callbacks. Latitude, longitude and zoom interpolate independently, so a low-zoom mid-flight
waypoint gives a dramatic zoom-out-then-in.

<style>.doc-content:has(.doc-demo){--doc-content-padding:0;--doc-content-overflow:visible}.doc-content:has(.doc-demo)>:not(.doc-demo):not(style){max-width:44rem;margin-inline:auto;padding-inline:1.25rem;box-sizing:border-box}.doc-demo .media{height:var(--tosi-view-size,70vh);position:relative;overflow:hidden;background:#0a0a12}.doc-demo .media>tosi-map{position:absolute;inset:0;width:100%;height:100%;pointer-events:none}</style>
<tosi-product class="doc-demo">
<tosi-product-section scroll="150">
<div class="media">
<tosi-map token="pk.eyJ1IjoicG9kcGVyc29uIiwiYSI6ImNqc2JlbWU0bjA1ZmY0YW5ycHZod3VhbWcifQ.arvqfpOqMgFYkKgQ35UScA" coords="37.4636,-122.4286,12" map-style="mapbox://styles/mapbox/satellite-streets-v12">
<tosi-scroll-map data-scroll-animate easing="ease-in-out">
<tosi-waypoint progress="0" coords="37.4636,-122.4286,12"></tosi-waypoint>
<tosi-waypoint progress="0.5" coords="51,0,2"></tosi-waypoint>
<tosi-waypoint progress="1" coords="65.0121,25.4651,12"></tosi-waypoint>
</tosi-scroll-map>
</tosi-map>
</div>
</tosi-product-section>
</tosi-product>

## Attributes

`<tosi-scroll-map>`
- **`easing`** — `"ease-in-out"` eases each segment (default: linear).

`<tosi-waypoint>`
- **`progress`** — 0 → 1 keyframe position.
- **`coords`** — `"lat,lng,zoom"` (the same triple `<tosi-map>` takes), or separate **`lat`** / **`lng`** / **`zoom`** attributes.

See also [`<tosi-scroll-camera>`](/tosi-b3d-scroll/) and [`<tosi-product>`](/tosi-product/).
*/
export class TosiScrollMap extends Component {
  static initAttributes = {
    easing: "",
  };

  static styleSpec = {
    ":host": { display: "none" },
  };

  content = () => slot();

  setScrollProgress(progress: number) {
    const map = findMap(this);
    if (!map) return;

    const wps = readMapWaypoints(this);
    if (wps.length === 0) return;

    const easing = this.getAttribute("easing") === "ease-in-out";

    let lat: number, lng: number, zoom: number;
    const first = wps[0];
    const last = wps[wps.length - 1];
    if (progress <= first.progress) {
      ({ lat, lng, zoom } = first);
    } else if (progress >= last.progress) {
      ({ lat, lng, zoom } = last);
    } else {
      let a = first;
      let b = last;
      for (let i = 0; i < wps.length - 1; i++) {
        if (progress >= wps[i].progress && progress <= wps[i + 1].progress) {
          a = wps[i];
          b = wps[i + 1];
          break;
        }
      }
      const raw = (progress - a.progress) / (b.progress - a.progress || 1);
      const t = easing ? easeInOutQuad(raw) : raw;
      lat = lerp(a.lat, b.lat, t);
      lng = lerp(a.lng, b.lng, t);
      zoom = lerp(a.zoom, b.zoom, t);
    }

    map.coords = `${lat.toFixed(6)},${lng.toFixed(6)},${zoom.toFixed(4)}`;
  }
}

export const tosiScrollMap = TosiScrollMap.elementCreator({
  tag: "tosi-scroll-map",
});

/*
Declaration merge: `initAttributes` keys become instance properties at hydration, which
the type system cannot infer from a static. tosijs 1.10 removed the `[key: string]: any`
index signature that used to paper over this, so these are now REAL types rather than
`any` — and a typo is a compile error.
*/
export interface TosiScrollMap extends ComponentAttrs<typeof TosiScrollMap.initAttributes> {}
